/**
 * NotFoundPage Component
 *
 * 404 error page displayed when a route is not found.
 * Provides a friendly message and link back to home.
 */

import { type CSSProperties } from 'react';
import { Link } from 'react-router-dom';

/**
 * NotFoundPage displays a 404 error with navigation back to home
 *
 * @example
 * ```tsx
 * // In router
 * <Route path="*" element={<NotFoundPage />} />
 * ```
 */
export const NotFoundPage = () => {
  return (
    <main style={containerStyle} role="main">
      <div style={contentStyle}>
        {/* 404 Icon */}
        <div style={iconContainerStyle}>
          <svg
            width="80"
            height="80"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M12 8V12M12 16H12.01"
              stroke="#9ca3af"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
              stroke="#9ca3af"
              strokeWidth="2"
            />
          </svg>
        </div>

        {/* Error Code */}
        <h1 style={errorCodeStyle}>404</h1>

        {/* Title */}
        <h2 style={titleStyle}>Page Not Found</h2>

        {/* Description */}
        <p style={descriptionStyle}>
          Sorry, we couldn&apos;t find the page you&apos;re looking for. It might have been moved,
          deleted, or never existed.
        </p>

        {/* Actions */}
        <div style={actionsStyle}>
          <Link to="/" style={primaryButtonStyle}>
            Go to Home
          </Link>
          <button type="button" onClick={() => window.history.back()} style={secondaryButtonStyle}>
            Go Back
          </button>
        </div>

        {/* Help Text */}
        <p style={helpTextStyle}>
          If you believe this is an error, please{' '}
          <a href="mailto:support@flash.app" style={linkStyle}>
            contact support
          </a>
          .
        </p>
      </div>
    </main>
  );
};

/**
 * Component styles
 */
const containerStyle: CSSProperties = {
  minHeight: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#f9fafb',
  padding: '20px',
};

const contentStyle: CSSProperties = {
  maxWidth: '400px',
  textAlign: 'center',
};

const iconContainerStyle: CSSProperties = {
  marginBottom: '24px',
  display: 'flex',
  justifyContent: 'center',
};

const errorCodeStyle: CSSProperties = {
  margin: 0,
  fontSize: '6rem',
  fontWeight: 700,
  color: '#d1d5db',
  lineHeight: 1,
};

const titleStyle: CSSProperties = {
  margin: '16px 0 8px 0',
  fontSize: '1.5rem',
  fontWeight: 600,
  color: '#1f2937',
};

const descriptionStyle: CSSProperties = {
  margin: '0 0 24px 0',
  fontSize: '1rem',
  color: '#6b7280',
  lineHeight: 1.6,
};

const actionsStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  marginBottom: '24px',
};

const primaryButtonStyle: CSSProperties = {
  display: 'inline-block',
  padding: '12px 24px',
  fontSize: '1rem',
  fontWeight: 600,
  color: '#ffffff',
  backgroundColor: '#3b82f6',
  border: 'none',
  borderRadius: '8px',
  textDecoration: 'none',
  cursor: 'pointer',
  transition: 'background-color 0.2s',
};

const secondaryButtonStyle: CSSProperties = {
  padding: '12px 24px',
  fontSize: '1rem',
  fontWeight: 500,
  color: '#374151',
  backgroundColor: '#ffffff',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'background-color 0.2s',
};

const helpTextStyle: CSSProperties = {
  margin: 0,
  fontSize: '0.875rem',
  color: '#9ca3af',
};

const linkStyle: CSSProperties = {
  color: '#3b82f6',
  textDecoration: 'none',
};

export default NotFoundPage;
